import { useMemo, useCallback, useEffect, useRef } from 'react'
import { useAuthContext } from '@contexts/AuthContext'
import { ADMIN_APP_KEYS, type AppKey } from '@/config/appKeys'

const EMPTY_ARRAY: string[] = []

interface AppAccessState {
  hasAccess: (appKey: AppKey) => boolean
  appAccess: string[]
  hasAnyAdminAccess: boolean
  isLoading: boolean
}

/**
 * Phase 76: Hook for app-level access gating.
 *
 * Replaces useAdminAuth for access checking. Uses a Set for O(1) lookups
 * instead of Array.includes(). Mirrors useAdminAuth pattern (useMemo, AuthContext).
 *
 * @returns {AppAccessState} App access state and helpers
 */
export function useAppAccess(): AppAccessState {
  const { user, isLoading } = useAuthContext()

  // Phase 76 M3b.12: Observability — warn once when user exists but app_access is missing
  const warnedRef = useRef(false)
  useEffect(() => {
    if (user && user.app_access === undefined && !isLoading && !warnedRef.current) {
      console.warn(
        '[useAppAccess] user exists but app_access is undefined — ' +
        'falling back to empty array. user_id=%s username=%s',
        user.id, user.username
      )
      warnedRef.current = true
    }
    if (!user) warnedRef.current = false
  }, [user, isLoading])

  // Serialize for content-based comparison — prevents unnecessary Set recreation
  // when session revalidation returns same app_access with a new array reference
  const appAccessKey = user?.app_access?.join(',') ?? ''

  const appAccessSet = useMemo(
    () => new Set(user?.app_access || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appAccessKey]
  )

  const hasAccess = useCallback(
    (appKey: AppKey) => appAccessSet.has(appKey),
    [appAccessSet]
  )

  // Convenience: does user have access to ANY admin app?
  const hasAnyAdminAccess = useMemo(
    () => ADMIN_APP_KEYS.some(app => appAccessSet.has(app)),
    [appAccessSet]
  )

  return {
    hasAccess,
    appAccess: user?.app_access ?? EMPTY_ARRAY,
    hasAnyAdminAccess,
    isLoading,
  }
}

export default useAppAccess
